import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import { staffApi, errMsg } from "@/components/staff/api";
import { AuthShell, Field, Status } from "@/components/staff/Shell";

const rules = (p) => {
  if (p.length < 12) return "Use at least 12 characters.";
  if (!/[a-zA-Z]/.test(p) || !/[0-9\W_]/.test(p)) return "Mix letters with numbers or symbols.";
  return "";
};

export default function StaffReset() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [status, setStatus] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    const problem = rules(form.password) || (form.password !== form.confirm ? "The two passwords don't match." : "");
    if (problem) return setStatus({ state: "error", msg: problem });
    setStatus({ state: "loading" });
    try {
      await staffApi.post("/auth/reset", { token, password: form.password });
      setStatus({ state: "done" });
    } catch (err) {
      setStatus({ state: "error", msg: await errMsg(err) });
    }
  };

  return (
    <AuthShell testId="staff-reset-page" title="Choose Your Password"
      intro="Pick a strong password you don't use anywhere else. A passphrase of 3-4 unrelated words works well.">
      {!token ? (
        <p className="text-sm text-fire" role="alert" data-testid="staff-reset-missing-token">This link is incomplete. Request a new one from the staff sign-in page.</p>
      ) : status.state === "done" ? (
        <div data-testid="staff-reset-done">
          <p className="text-sm text-navy">Your password is saved. Next, sign in and set up two-step sign-in if you haven't yet.</p>
          <Link to="/staff" className="btn btn-navy mt-6 w-full" data-testid="staff-reset-signin-link">Go to Staff Sign In</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-6" data-testid="staff-reset-form">
          <Field label="New password" type="password" required autoComplete="new-password" hint="At least 12 characters, mixing letters with numbers or symbols."
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-reset-password-input" />
          <Field label="Confirm new password" type="password" required autoComplete="new-password"
            value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="staff-reset-confirm-input" />
          <Status status={status} testId="staff-reset-status" />
          <button type="submit" disabled={status.state === "loading"} className="btn btn-navy w-full disabled:opacity-60" data-testid="staff-reset-submit-button">
            {status.state === "loading" ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Save Password
          </button>
        </form>
      )}
    </AuthShell>
  );
}
